<template>
  <div id="app" :class="{ 'app-about-route': isAboutRoute, 'app-roboto-route': !isAboutRoute }">
    <StoreHeader />
    <Alert />
    <router-view class="app-route-view" />
    <TimedLeadPopups />
    <SupportChatWidget />
  </div>
</template>

<script>
import StoreHeader from '@/components/NavBar/StoreHeader.vue'
import Alert from '@/components/ui/alert.vue'
import SupportChatWidget from '@/components/support/SupportChatWidget.vue'
import TimedLeadPopups from '@/components/lead/TimedLeadPopups.vue'
import { buildRouteSeo } from '@/utils/seo'

export default {
  components: { StoreHeader, Alert, TimedLeadPopups, SupportChatWidget },
  metaInfo() {
    return buildRouteSeo(this.$route)
  },
  computed: {
    isAboutRoute() {
      return this.$route.path === '/about'
    }
  },
  watch: {
    '$route.path': {
      immediate: true,
      handler(path) {
        const background = path === '/about' ? '#000000' : '#ffffff'
        document.documentElement.style.backgroundColor = background
        document.body.style.backgroundColor = background
      }
    }
  },
  async mounted() {
    this.$store.dispatch('INIT_FAVORITES')
    await this.$store.dispatch('GET_EQUIPMENT')
    this.$store.dispatch('GET_SITE_SETTINGS')
    const user = {
      email: localStorage.getItem('email'),
      hash: localStorage.getItem('user_hash')
    }
    await this.$store.dispatch('RELOG_USER', user)
    this.$store.dispatch('GET_CART', this.$store.getters.equipment)
  }
}
</script>

<style>
:root {
  --site-container: min(1760px, calc(100% - 56px));
  --site-container-tablet: min(1120px, calc(100% - 32px));
  --site-container-mobile: calc(100% - 20px);
  --site-primary: #b07c50;
  --site-primary-dark: #8f603b;
  --primary: var(--site-primary);
  --primary-dark: var(--site-primary-dark);
  --site-ink: #201b17;
  --site-text: #3e3731;
  --site-muted: #7d746d;
  --site-line: #e9e0d8;
  --site-soft: #faf7f3;
}

html,
body,
#app {
  min-height: 100%;
}

html {
  overflow-x: hidden;
  background: #fff;
  font-size: 10px;
}

body {
  min-height: 100vh;
  min-height: 100dvh;
  margin: 0;
  padding: 0;
  background: #fff;
}

#app {
  display: flex;
  min-height: 100vh;
  min-height: 100dvh;
  flex-direction: column;
  color: var(--site-text);
  text-align: center;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#app.app-about-route {
  background: #000;
  color: #fff;
  font-family: Arial, Avenir, Helvetica, Arial, sans-serif;
}

#app.app-about-route > .app-route-view,
#app.app-about-route > .app-route-view button,
#app.app-about-route > .app-route-view input,
#app.app-about-route > .app-route-view textarea,
#app.app-about-route > .app-route-view select {
  font-family: Arial, Avenir, Helvetica, Arial, sans-serif;
}

#app.app-roboto-route,
#app.app-roboto-route button,
#app.app-roboto-route input,
#app.app-roboto-route textarea,
#app.app-roboto-route select {
  font-family: 'Roboto', Arial, Helvetica, sans-serif;
}

#app.app-roboto-route .app-route-view,
#app.app-roboto-route .app-route-view * {
  font-family: 'Roboto', Arial, Helvetica, sans-serif;
}

.app-route-view {
  width: 100%;
  min-height: 0;
  flex: 1 0 auto;
}

#app.app-roboto-route > .app-route-view {
  display: flex;
  min-height: calc(100dvh - 118px);
  flex-direction: column;
}

#app.app-roboto-route .sofa-shell,
#app.app-roboto-route .site-footer__inner,
#app.app-roboto-route .site-footer__bottom,
#app.app-roboto-route .reviews-shell,
#app.app-roboto-route .contacts-main,
#app.app-roboto-route .news-shell,
#app.app-roboto-route .article-hero__inner,
#app.app-roboto-route .article-layout,
#app.app-roboto-route .product-shell,
#app.app-roboto-route .favorites-shell,
#app.app-roboto-route .admin-shell,
#app.app-roboto-route .privacy__container,
#app.app-roboto-route .agreement__container,
#app.app-roboto-route .offer-shell,
#app.app-roboto-route .news-main,
#app.app-roboto-route .auth-shell,
#app.app-roboto-route .profile-shell,
#app.app-roboto-route .cart__main,
#app.app-roboto-route .empty-cart,
#app.app-roboto-route .success {
  width: var(--site-container);
  max-width: none;
  margin-right: auto;
  margin-left: auto;
}

@media (max-width: 1180px) {
  #app.app-roboto-route .sofa-shell,
  #app.app-roboto-route .site-footer__inner,
  #app.app-roboto-route .site-footer__bottom,
  #app.app-roboto-route .reviews-shell,
  #app.app-roboto-route .contacts-main,
  #app.app-roboto-route .news-shell,
  #app.app-roboto-route .article-hero__inner,
  #app.app-roboto-route .article-layout,
  #app.app-roboto-route .product-shell,
  #app.app-roboto-route .favorites-shell,
  #app.app-roboto-route .admin-shell,
  #app.app-roboto-route .privacy__container,
  #app.app-roboto-route .agreement__container,
  #app.app-roboto-route .offer-shell,
  #app.app-roboto-route .news-main,
  #app.app-roboto-route .auth-shell,
  #app.app-roboto-route .profile-shell,
  #app.app-roboto-route .cart__main,
  #app.app-roboto-route .empty-cart,
  #app.app-roboto-route .success { width: var(--site-container-tablet); }
}

@media (max-width: 680px) {
  #app.app-roboto-route .sofa-shell,
  #app.app-roboto-route .site-footer__inner,
  #app.app-roboto-route .site-footer__bottom,
  #app.app-roboto-route .reviews-shell,
  #app.app-roboto-route .contacts-main,
  #app.app-roboto-route .news-shell,
  #app.app-roboto-route .article-hero__inner,
  #app.app-roboto-route .article-layout,
  #app.app-roboto-route .product-shell,
  #app.app-roboto-route .favorites-shell,
  #app.app-roboto-route .admin-shell,
  #app.app-roboto-route .privacy__container,
  #app.app-roboto-route .agreement__container,
  #app.app-roboto-route .offer-shell,
  #app.app-roboto-route .news-main,
  #app.app-roboto-route .auth-shell,
  #app.app-roboto-route .profile-shell,
  #app.app-roboto-route .cart__main,
  #app.app-roboto-route .empty-cart,
  #app.app-roboto-route .success { width: var(--site-container-mobile); }
}

* {
  box-sizing: border-box;
}

button,
a,
input,
textarea,
select {
  -webkit-tap-highlight-color: transparent;
}

button {
  transition: background-color .22s ease, border-color .22s ease, color .22s ease, transform .22s ease, opacity .22s ease;
}

.button-1 {
  width: 100%;
  padding: 1rem;
  border: 0;
  border-radius: .5rem;
  background: var(--site-primary);
  color: #fff;
  font-size: 1.2rem;
  font-weight: 700;
  cursor: pointer;
}

.button-1:hover {
  background: var(--site-primary-dark);
}

@font-face {
  font-family: Pattaya;
  src: url(@/assets/fonts/Pattaya-Regular.ttf);
}

@font-face {
  font-family: Caveat;
  font-variation-settings: "wght" 700;
  src: url(@/assets/fonts/Caveat/Caveat-VariableFont_wght.ttf);
}

@font-face {
  font-family: ssmed;
  src: url(@/assets/fonts/ssmed/ssmed-Regular.woff2);
}
</style>
